import React, { useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../../lib/utils";

/**
 * Custom grey tooltip (not the native browser title tooltip).
 * Clones a single child and shows a fixed portal tooltip on hover/focus.
 */
export function GreyTooltip({ content, children, className }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  const child = React.Children.only(children);
  const hasContent = Boolean(content && String(content).trim());

  const placeFromEvent = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const preferMouseX = rect.width > 160;
    setCoords({
      top: rect.top - 6,
      left: preferMouseX ? e.clientX : rect.left + rect.width / 2,
    });
  }, []);

  const handleEnter = useCallback(
    (e) => {
      child.props.onMouseEnter?.(e);
      if (!hasContent) return;
      placeFromEvent(e);
      setOpen(true);
    },
    [child.props, hasContent, placeFromEvent]
  );

  const handleMove = useCallback(
    (e) => {
      child.props.onMouseMove?.(e);
      if (!hasContent || !open) return;
      placeFromEvent(e);
    },
    [child.props, hasContent, open, placeFromEvent]
  );

  const handleLeave = useCallback(
    (e) => {
      child.props.onMouseLeave?.(e);
      setOpen(false);
    },
    [child.props]
  );

  const handleFocus = useCallback(
    (e) => {
      child.props.onFocus?.(e);
      if (!hasContent) return;
      placeFromEvent(e);
      setOpen(true);
    },
    [child.props, hasContent, placeFromEvent]
  );

  const handleBlur = useCallback(
    (e) => {
      child.props.onBlur?.(e);
      setOpen(false);
    },
    [child.props]
  );

  if (!hasContent) return child;

  return (
    <>
      {React.cloneElement(child, {
        onMouseEnter: handleEnter,
        onMouseMove: handleMove,
        onMouseLeave: handleLeave,
        onFocus: handleFocus,
        onBlur: handleBlur,
      })}
      {open
        ? createPortal(
            <div
              role="tooltip"
              className={cn(
                "pointer-events-none fixed z-[200] max-w-xs -translate-x-1/2 -translate-y-full",
                "rounded-md bg-gray-500 px-2.5 py-1.5 text-[11px] font-medium leading-snug text-white shadow-md",
                className
              )}
              style={{ top: coords.top, left: coords.left }}
            >
              {content}
            </div>,
            document.body
          )
        : null}
    </>
  );
}
